import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from "@angular/core";
import { AvailabilityVisitService } from "@app/services/http/availability-visit.service";
import { ActivatedRoute, Router } from "@angular/router";
import { VisitStore } from "@app/services/store/visit-store";
import { Visit_DetailProjection } from "@app/models/rest/visit";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { BookingConfirmComponent } from "@app/view/booking/booking-confirm/booking-confirm.component";
import { VisitStatus } from "@app/models/enums/visit-status.enum";
import { ResourceStore } from "@app/services/store/resource-store";
import { TicketStore } from "@app/services/store/ticket-store";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { NgxUiLoaderModule, NgxUiLoaderService } from "ngx-ui-loader";
import { SolverService } from "@app/services/rest/solver.service";
import { BookingOutcomeComponent } from "@app/view/booking/booking-outcome/booking-outcome.component";
import { Location } from "@angular/common";
import { BookingNavigationService } from "@app/services/navigation/booking-navigation.service";
import { ResourceChooserDialogComponent } from "@app/view/shared/resource-chooser-dialog/resource-chooser-dialog.component";
import { TicketAvailabilityRequest } from "@app/models/dto/ticket-availability-request";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { Ticket_BasicProjection } from "@app/models/rest/ticket";
import moment from "moment";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import {
  MatList,
  MatListItem,
  MatListSubheaderCssMatStyler
} from "@angular/material/list";
import { MatLine } from "@angular/material/core";
import { MatDivider } from "@angular/material/divider";
import { VisitPropertyListComponentComponent } from "../visit-property-list-component/visit-property-list-component.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MomentModule } from "ngx-moment";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";

@Component({
  selector: "app-visit-floating",
  templateUrl: "./visit-floating.component.html",
  styleUrls: ["./visit-floating.component.scss"],
  imports: [
    NgxUiLoaderModule,
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatButton,
    MatList,
    MatListSubheaderCssMatStyler,
    MatListItem,
    MatLine,
    MatDivider,
    VisitPropertyListComponentComponent,
    MatTooltip,
    MomentModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule
  ]
})
export class VisitFloatingComponent implements OnInit {
  availabilityService = inject(AvailabilityVisitService);
  visitStore = inject(VisitStore);
  resourceStore = inject(ResourceStore);
  ticketStore = inject(TicketStore);
  dialog = inject(MatDialog);
  private router = inject(Router);
  private activeRoute = inject(ActivatedRoute);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  private snackBar = inject(MatSnackBar);
  private ngxLoader = inject(NgxUiLoaderService);
  private location = inject(Location);
  private solverServer = inject(SolverService);
  private store = inject<Store<any>>(Store);
  private clientConfig = inject(ClientConfigService);
  private bookingNavigationService = inject(BookingNavigationService);

  @Input()
  public exitOnCompletion: boolean = false;
  public closed: boolean = false;
  visit: Visit_DetailProjection;
  public calculating: boolean = false;
  public windowClose = false;
  public unsuitable: boolean = false;
  public allowResourceLocking: boolean = true;
  public floatingBookingDisabled: boolean = false;
  currentLoadingMessage: string = "Loading Visit...";
  resourceList: { description: string; ticket: Ticket_BasicProjection }[];
  resourceFormControl: UntypedFormControl[] = [];

  // New properties for date range booking
  dateRange = new UntypedFormGroup({
    startDate: new UntypedFormControl(null, Validators.required),
    endDate: new UntypedFormControl(null, Validators.required)
  });

  // Availability check result
  availabilityChecked: boolean = false;
  availabilityFeasible: boolean = false;
  datesModifiedAfterCheck: boolean = false;

  @Output()
  booked = new EventEmitter<object>();

  constructor() {
    this.activeRoute.params.subscribe((params) => {
      if (params && params["id"]) {
        this.visitId = params["id"];
      }
    });
    this.activeRoute.data.subscribe((value) => {
      console.log("route data", value);
      if (value["visitOnlyBooking"]) {
        this.exitOnCompletion = true;
        this.windowClose = true;
      }
    });

    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.allowResourceLocking = !!auth && auth.allowResourceLocking;
    });
  }

  private _visitId: number;

  get visitId(): number {
    return this._visitId;
  }

  @Input()
  set visitId(value: number) {
    this._visitId = value;
    this.loadVisit();
  }

  get isOverdue(): boolean {
    if (!this.visit) {
      return false;
    }
    return moment(this.visit.job.planningWindowEnd).isBefore(moment());
  }

  ngOnInit() {
    this.currentLoadingMessage = "Loading data";
    console.log("init fired");

    // Check if floating booking is enabled in client config
    if (this.clientConfig && this.clientConfig.config) {
      this.floatingBookingDisabled =
        !this.clientConfig.config.enableFloatingBooking;

      if (this.floatingBookingDisabled) {
        this.unsuitable = true; // Use existing unsuitable flag to block the UI
      }
    }

    // Store original route if not already stored (for cases where user navigates directly to floating booking)
    if (!this.bookingNavigationService.hasOriginalRoute()) {
      // Try to determine the referrer route
      const currentUrl = this.router.url;
      if (currentUrl.includes("/booking/launch/")) {
        // This is a standalone booking, no need to store route as exitOnCompletion will handle it
      } else {
        // This is embedded in the main app, store a fallback route
        this.bookingNavigationService.setOriginalRoute("/");
      }
    }

    // Set up change listeners for the date controls to reset availability check
    this.dateRange.get("startDate").valueChanges.subscribe(() => {
      this.resetAvailabilityCheck();
    });

    this.dateRange.get("endDate").valueChanges.subscribe(() => {
      this.resetAvailabilityCheck();
    });
  }

  // The end of the window is exclusive: midnight at the start of the day after
  // the selected end date. A same-day booking then spans a full day rather than
  // ending at 23:59, which Core cannot map to a planning window (#1445).
  private getBookingWindow(): {
    startDate: moment.Moment;
    endDate: moment.Moment;
  } {
    const startDate = moment(this.dateRange.get("startDate").value).startOf(
      "day"
    );
    const endDate = moment(this.dateRange.get("endDate").value)
      .startOf("day")
      .add(1, "day");
    return { startDate, endDate };
  }

  // Reset availability check when dates change
  resetAvailabilityCheck() {
    if (this.availabilityChecked) {
      this.datesModifiedAfterCheck = true;
    }
    this.availabilityChecked = false;
    this.availabilityFeasible = false;
  }

  getTicketResourceLocks(): TicketAvailabilityRequest[] {
    const ticketOutput: TicketAvailabilityRequest[] = [];
    if (!this.visit || !this.visit.ticketList) {
      return ticketOutput;
    }

    this.visit.ticketList.forEach((value) => {
      if (
        this.resourceFormControl[value.id] &&
        this.resourceFormControl[value.id].value
      ) {
        ticketOutput.push({
          ticketId: value.id,
          resourceId: this.resourceFormControl[value.id].value.id
        });
      }
    });
    return ticketOutput;
  }

  checkAvailability() {
    if (!this.dateRange.valid) {
      this.snackBar.open("Please select both start and end dates", "Close", {
        duration: 3000
      });
      return;
    }

    const { startDate, endDate } = this.getBookingWindow();
    if (!endDate.isAfter(startDate)) {
      this.snackBar.open(
        "The end date must not be before the start date",
        "Close",
        {
          duration: 3000
        }
      );
      return;
    }

    this.calculating = true;
    this.currentLoadingMessage = "Checking availability...";
    this.ngxLoader.startLoader("bookingLoader", "availability");

    // Reset the modification flag when explicitly checking availability
    this.datesModifiedAfterCheck = false;

    this.availabilityService
      .bookVisit({
        visitId: this.visit.id,
        startTime: startDate.toDate(),
        endTime: endDate.toDate(),
        force: false,
        bookingReasonId: null,
        notes: null,
        displayedSlotRanking: null,
        refer: false,
        cancel: false,
        tickets: this.getTicketResourceLocks(),
        skipSave: true, // Added parameter to validate without saving
        floating: true // Added parameter to indicate floating booking
      })
      .then((response) => {
        this.availabilityChecked = true;
        this.availabilityFeasible = response.success;

        this.calculating = false;
        this.ngxLoader.stopLoader("bookingLoader", "availability");
      })
      .catch((error) => {
        this.availabilityChecked = true;
        this.availabilityFeasible = false;
        this.calculating = false;
        this.ngxLoader.stopLoader("bookingLoader", "availability");
        this.serviceErrorHandler.handleError(error);
      });
  }

  confirmBooking() {
    // Don't allow booking if availability check hasn't been successful
    if (!this.availabilityFeasible) {
      return;
    }

    // Don't allow booking if dates were modified after checking availability
    if (this.datesModifiedAfterCheck) {
      this.snackBar.open(
        "Please check availability again with the new date range before confirming",
        "Close",
        {
          duration: 5000
        }
      );
      return;
    }

    const dialogRef = this.dialog.open(BookingConfirmComponent, {
      width: "450px",
      data: {
        mode: "EDIT",
        visit: this.visit,
        floatingMode: true,
        forceEnabled: false // Explicitly disable force booking mode
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed");
      console.log(result);
      if (result && result.result === true) {
        console.log("booking confirmed");
        this.currentLoadingMessage = "Confirming booking...";
        this.ngxLoader.startLoader("bookingLoader", "confirm");

        const { startDate, endDate } = this.getBookingWindow();

        this.availabilityService
          .bookVisit({
            visitId: this.visit.id,
            startTime: startDate.toDate(),
            endTime: endDate.toDate(),
            force: result.force || false,
            bookingReasonId: result.bookingReasonId,
            notes: result.notes,
            displayedSlotRanking: null,
            refer: false,
            cancel: false,
            tickets: this.getTicketResourceLocks(),
            skipSave: false, // Actually save the booking
            floating: true // Indicate floating booking
          })
          .then((response) => {
            this.ngxLoader.stopLoader("bookingLoader", "confirm");
            let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
              data: {
                success: response.success,
                message: response.message
              }
            });

            bookingOutcomeRef.afterClosed().subscribe(() => {
              if (response.success) {
                this.closed = true; // Mark as closed to prevent further interactions
                this.booked.emit({ booked: true });
                this.ngxLoader.stopLoader("bookingLoader", "confirm");

                if (this.exitOnCompletion) {
                  if (this.windowClose) {
                    console.log("Window is closing");
                    window.close();
                  }
                } else {
                  // Use setTimeout to ensure the navigation happens after the current execution
                  setTimeout(() => {
                    this.bookingNavigationService.returnToOriginalRoute();
                    this.bookingNavigationService.clearOriginalRoute();
                  }, 100);
                }
              } else {
                this.ngOnInit(); // Only reload the component if booking failed
              }
            });
          })
          .catch((error) => {
            this.ngxLoader.stopLoader("bookingLoader", "confirm");
            let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
              data: {
                success: false,
                message: "Error in booking service call"
              }
            });
            bookingOutcomeRef.afterClosed().subscribe(() => {
              this.booked.emit({ booked: false });
              this.serviceErrorHandler.handleError(error);
            });
          });
      }
    });
  }

  returnToMenu() {
    this.router.navigate(["../booking"]);
  }

  switchToStandardBooking() {
    // Preserve the current routing context based on how this component was loaded
    if (this.exitOnCompletion) {
      this.router.navigate([`/booking/launch/visit/${this.visitId}`]);
    } else {
      // When embedded in the main app, use relative navigation
      this.router.navigate([`../../../visit/${this.visitId}`], {
        relativeTo: this.activeRoute
      });
    }
  }

  selectResource(ticket: Ticket_BasicProjection) {
    const skills = [];
    ticket?.skillProfile?.skillRuleList?.forEach((skillRule) => {
      skills.push(skillRule.skill.id);
    });
    const dialogRef = this.dialog.open(ResourceChooserDialogComponent, {
      data: {
        requiredZoneId: this.visit?.job?.zone?.id,
        requiredSkillIds: skills
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed", result);
      this.resourceFormControl[ticket.id].setValue(result.data);
      this.loadVisit();
    });
  }

  clearSelection(ticket: Ticket_BasicProjection) {
    this.ticketStore.loadSingle(ticket.id).then((storedTicket) => {
      storedTicket.unbindRelation("lockToResource").subscribe(() => {
        console.log("set");
        this.resourceFormControl[ticket.id].setValue(null);
        this.loadVisit();
      });
    });
  }

  openReferralForm() {
    const dialogRef = this.dialog.open(BookingConfirmComponent, {
      width: "450px",
      data: {
        mode: "EDIT",
        visit: this.visit,
        referralMode: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("Got result: ", result);
      if (result && result.result) {
        this.availabilityService
          .bookVisit({
            visitId: this.visit.id,
            startTime: null,
            endTime: null,
            force: false,
            bookingReasonId: result.bookingReasonId,
            notes: result.notes,
            displayedSlotRanking: null,
            refer: true,
            cancel: false,
            tickets: null
          })
          .then((response) => {
            this.ngxLoader.stopLoader("bookingLoader", "confirm");
            let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
              data: {
                success: response.success,
                message: response.message,
                mode: "REFER"
              }
            });
            bookingOutcomeRef.afterClosed().subscribe(() => {
              console.log(response.success);
              if (response.success) {
                if (this.exitOnCompletion) {
                  console.log("Exit on completion mode");
                  if (this.windowClose) {
                    console.log("Window is closing");
                    window.close();
                  }
                  this.booked.emit({ booked: true });
                  this.ngxLoader.stopLoader("bookingLoader", "confirm");
                  this.ngOnInit();
                } else {
                  this.bookingNavigationService.returnToOriginalRoute();
                  this.bookingNavigationService.clearOriginalRoute();
                }
              } else {
                this.ngOnInit();
              }
            });
          })
          .catch(() => {
            let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
              data: {
                success: false,
                message: "Error in booking service call"
              }
            });
            bookingOutcomeRef.afterClosed().subscribe(() => {
              this.booked.emit({ booked: false });
              this.ngxLoader.stopLoader("bookingLoader", "confirm");
            });
          });
      }
    });
  }

  getVisitDetails(visitId: number) {
    this.calculating = true;
    this.visit = null;
    this.resourceStore
      .loadAll({ params: { projection: "detail" } })
      .then(() => {
        this.visitStore
          .loadSingleWithSearch("findVisitByIdEquals", {
            params: {
              projection: "detail",
              id: visitId
            }
          })
          .then((halVisit: Visit_DetailProjection) => {
            this.visit = halVisit;

            if (
              halVisit.visitStatus === VisitStatus.CANCELLED ||
              halVisit.visitStatus === VisitStatus.IN_PROGRESS ||
              halVisit.visitStatus === VisitStatus.COMPLETED
            ) {
              this.calculating = false;
              this.unsuitable = true;
            }

            // Pre-populate date range with job planning window
            if (this.visit.job) {
              this.dateRange.patchValue({
                startDate: moment(this.visit.job.planningWindowStart).toDate(),
                endDate: moment(this.visit.job.planningWindowEnd).toDate()
              });
            }

            this.calculating = false;

            this.resourceList = [];
            if (this.visit.ticketList) {
              this.visit.ticketList.forEach((value, index) => {
                this.resourceList.push({
                  description: "Resource " + (index + 1),
                  ticket: value
                });
              });

              this.visit.ticketList.forEach((ticket) => {
                console.log("visit ticket", ticket);
                if (!this.resourceFormControl[ticket.id]) {
                  this.resourceFormControl[ticket.id] = new UntypedFormControl(
                    ticket.lockToResource,
                    Validators.nullValidator
                  );
                }
              });
            }
          });
      });
  }

  private loadVisit() {
    console.log("visit id changed to " + this.visitId);
    this.getVisitDetails(this.visitId);
  }
}
